/**
 * useScrollReveal
 *
 * Reveal-once scroll-driven visibility hook backed by IntersectionObserver.
 *
 * Returns:
 *   { ref, isVisible }
 *     - ref: React ref to attach to the element you want to observe
 *     - isVisible: false until the element crosses the viewport (10% threshold),
 *                  then permanently true
 *
 * Reveal-once semantics:
 *   On first intersection, the hook sets `isVisible: true` and calls
 *   `observer.unobserve(target)`. The element will NOT toggle back to false
 *   when scrolled out of view.
 *
 * Reduced-motion short-circuit:
 *   If `usePrefersReducedMotion()` returns `true`, the hook sets
 *   `isVisible: true` immediately and never instantiates the observer.
 *   Reduced-motion users see all content rendered without animation gating.
 *
 * StrictMode safety:
 *   The IntersectionObserver is created INSIDE the useEffect and disconnected
 *   in the cleanup. StrictMode's mount → unmount → mount cycle correctly tears
 *   down the first observer and creates a fresh one on the second mount.
 *
 * Generic usage:
 *   const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
 *   <div ref={ref} className={isVisible ? 'is-visible' : ''}>...</div>
 */

import { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

export const useScrollReveal = <T extends HTMLElement = HTMLElement>(): {
  ref: React.RefObject<T | null>;
  isVisible: boolean;
} => {
  const ref = useRef<T | null>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isVisible, setIsVisible] = useState<boolean>(prefersReducedMotion);

  useEffect(() => {
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }
    const target = ref.current;
    if (!target) return;

    const handleIntersect = (entries: IntersectionObserverEntry[]): void => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, { threshold: 0.1 });
    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [prefersReducedMotion]);

  return { ref, isVisible };
};
